import { inject, Injectable } from '@angular/core';

import { type RuntimeContext } from '@blaze-shared/elements-domain';
import { IntelliSenseService } from '@blaze-shared/intellisense-feature';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { type WorkflowSchema } from '@blaze-shared/schema-domain';
import { type SaveFileWorkflowNode } from '@blaze-shared/workflow-domain';

import { normalizeNodeInput } from '../../node-input-rows';

import { buildCsvFileContent } from './csv-file-content';

/** Long enough for the browser to have started the download before the blob URL is released. */
const OBJECT_URL_LIFETIME_MS = 100;

/**
 * Writes the node's input to a file and hands it to the browser to save.
 *
 * The file is built in the page rather than fetched, so nothing leaves the browser. A node with no
 * rows saves nothing at all — an empty file would only look like a failed export.
 */
@Injectable({ providedIn: 'root' })
export class SaveFileActionService {
  private readonly iss = inject(IntelliSenseService);
  private readonly schemaService = inject(SchemaService);

  saveFile(
    node: SaveFileWorkflowNode,
    nodeInput: any,
    ctx: RuntimeContext,
    schema?: WorkflowSchema,
  ): void {
    if (!nodeInput) {
      return;
    }
    const data = normalizeNodeInput(nodeInput);
    if (!data.length) {
      return;
    }

    const isCsv = node?.fileType?.toLowerCase() === 'csv';
    const blob = new Blob(isCsv ? this.buildCsvContent(node, data, schema) : [], {
      type: isCsv ? 'text/csv' : 'text/plain',
    });
    const url = URL.createObjectURL(blob);

    const anchor = document.createElement('a');
    anchor.setAttribute('href', url);
    anchor.setAttribute('download', this.resolveFilename(node, ctx) ?? 'blaze-file');
    anchor.click();
    setTimeout(() => URL.revokeObjectURL(url), OBJECT_URL_LIFETIME_MS);
  }

  private buildCsvContent(
    node: SaveFileWorkflowNode,
    data: any[],
    schema?: WorkflowSchema,
  ): string[] {
    return buildCsvFileContent({
      chooseColumns: node.chooseColumns,
      columns: node.columns || [],
      data,
      resolveColumnName: (columnIdOrName) => this.schemaService.getColumnName(columnIdOrName) || '',
      schema,
    });
  }

  /** The node's filename may itself be an expression, which the `$$`/`@@` markers give away. */
  private resolveFilename(node: SaveFileWorkflowNode, ctx: RuntimeContext): string | undefined {
    const nodeFilename = this.schemaService.replaceTagIdsWithValuesWhenString(node.filename);
    return nodeFilename?.includes('$$') || nodeFilename?.includes('@@')
      ? this.iss.eval(nodeFilename, ctx)
      : nodeFilename;
  }
}
